/** @file Hook: connects a rig panel to the working document, the edit path, the mode and the playhead. */
import type { CommandCall, Warning } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import { useCallback, useRef, useState } from 'react';
import { useStore } from 'zustand';
import { dispatch } from '../../api/sync/dispatch';
import { useT } from '../../shell/i18n/useT';
import { currentDoc } from '../../stores/currentDoc';
import { docStore } from '../../stores/docStore';
import { playheadStore } from '../../stores/playheadStore';
import { toolStore } from '../../stores/toolStore';
import { editTarget } from './editTarget';
import type { RigView } from './rigView';

/** A rig panel's connection: the view (null without an open project), the last refusal and the last batch's warnings. */
export interface RigPanelState {
  t: Translate;
  view: RigView | null;
  failure: { thrown: unknown } | null;
  notes: readonly Warning[];
  dismiss: () => void;
}

/** One dispatch per user action; its refusal or warnings stay shown until the next action or Dismiss. */
export function useRigPanel(): RigPanelState {
  const t = useT();
  const doc = useStore(docStore, currentDoc);
  const mode = useStore(toolStore, (s) => s.mode);
  const animation = useStore(playheadStore, (s) => s.animation);
  const playing = useStore(playheadStore, (s) => (s.playing ? null : s.frame));
  const lastFrame = useRef(0);
  if (playing !== null) lastFrame.current = playing;
  const [failure, setFailure] = useState<{ thrown: unknown } | null>(null);
  const [notes, setNotes] = useState<readonly Warning[]>([]);
  const run = useCallback((calls: readonly CommandCall[]) => {
    setFailure(null);
    setNotes([]);
    try {
      dispatch([...calls]).done.then((reply) => { setNotes(reply.warnings); }, (thrown: unknown) => { setFailure({ thrown }); });
    } catch (thrown) {
      setFailure({ thrown });
    }
  }, []);
  const dismiss = useCallback(() => { setFailure(null); setNotes([]); }, []);
  const view = doc === null ? null : { doc, t, run, target: editTarget(mode, { animation, frame: lastFrame.current }, doc) };
  return { t, view, failure, notes, dismiss };
}
